import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDebounce } from "../../hooks/DebounceHook";
import "../../styles/datepicker.css";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";

export const GaDatePickerInput = ({ onChange, value, disabled }: { onChange: (inputDate: Date | undefined) => any, value: Date | undefined, disabled?: boolean }) => {
    const [selected, setSelected] = useState<Date | undefined>(value);
    const [currentDate, setCurrentDate] = useState<Date>(value || new Date());
    const debouncedSelect = useDebounce<Date | undefined>(selected, 500);
    const [isOpen, setIsOpen] = useState(false);
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const [showYearPicker, setShowYearPicker] = useState(false);
    const datePickerRef = useRef<DatePicker>(null);

    useEffect(() => {
        onChange(selected);
        setCurrentDate(value || new Date());
    }, [debouncedSelect]);

    const handleChange = (date: Date | null) => {
        if (!showMonthPicker && !showYearPicker) {
            setSelected(date ?? undefined);
            setCurrentDate(date ?? new Date());
        }
        
        if (showMonthPicker)
            setShowMonthPicker(false);

        if (showYearPicker) {
            if (datePickerRef.current?.calendar) {
                if (selected)
                    datePickerRef.current.calendar.changeMonth(selected.getMonth());
                if (date)
                    datePickerRef.current.calendar.changeYear(date.getFullYear());
            }
            setShowYearPicker(false);
        }
    }

    useEffect(() => {
        if (selected === value)
            return;

        setSelected(value);
        setShowMonthPicker(false);
        setShowYearPicker(false);
    }, [value]);

    const handlePreviousMonth = () => {
        if (datePickerRef.current?.calendar)
            datePickerRef.current.calendar.decreaseMonth();
    };

    const handleNextMonth = () => {
        if (datePickerRef.current?.calendar)
            datePickerRef.current.calendar.increaseMonth();
    };

    const GaDatepickerHeader = ({ date }: { date: Date; }) => {
        const month = new Date(date).toLocaleString('default', { month: 'long' });
        const year = new Date(date).getFullYear();
        
        function onChangeMonth(): void {
            setShowYearPicker(false);
            setShowMonthPicker(true);
        }

        function onChangeYear(): void {
            setShowMonthPicker(false);
            setShowYearPicker(true);
        }

        return (
          <div className="d-flex">
            
            <div className="ga-ar-header-select">
                <div onClick={onChangeMonth}>
                    {month} 
                    {showMonthPicker && <ChevronUp size={16} color="var(--ga-color-icon-primary)" />}
                    {!showMonthPicker && <ChevronDown size={16} color="var(--ga-color-icon-primary)" />}
                </div>
                <div onClick={onChangeYear}>
                    {year} 
                    {showYearPicker && <ChevronUp size={16} color="var(--ga-color-icon-primary)" />}
                    {!showYearPicker && <ChevronDown size={16} color="var(--ga-color-icon-primary)" />}
                </div>
            </div>
            {!showMonthPicker &&
                 <div className="ga-ar-datepicker-nav">
                    <ChevronLeft onClick={handlePreviousMonth} color="var(--ga-color-icon-primary)" />
                    <ChevronRight onClick={handleNextMonth} color="var(--ga-color-icon-primary)" />
                </div>
            }
          </div>
        );
      };

    return (
        <div className="col-md-auto px-0">
            <div className="ga-ar-datepicker-icon"><CalendarDays /></div>
            <DatePicker
                ref={datePickerRef}
                className="ga-input"
                calendarClassName="ga-ar-datepicker"
                selected={selected}
                onChange={(date: Date | null) => handleChange(date)}
                showPopperArrow={false}
                calendarStartDay={1}
                showMonthYearPicker={showMonthPicker}
                showYearPicker={showYearPicker}
                disabled={disabled}
                dateFormat="P"
                renderCustomHeader={(props) => <GaDatepickerHeader {...props} date={currentDate} />}
                open={isOpen}
                onClickOutside={() => setIsOpen(false)}
                onFocus={() => setIsOpen(true)}
                onMonthChange={(date) => setCurrentDate(date)}
                onYearChange={(date) => setCurrentDate(date)}
            />
        </div>
    )
}